import { KeyboardEvent, useRef } from "react";
import "components/ToggleGroup/index.css";

/** Which senders the view lists: people on file, or numbers and emails that aren't. */
export type Senders = "known" | "unknown";

const OPTIONS: { value: Senders; label: string }[] = [
  { value: "known", label: "Known" },
  { value: "unknown", label: "Unknown" },
];

const NEXT: Record<string, number> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
};

/**
 * Known / Unknown senders as one segmented control, a radio group: both
 * options keep their outline and the chosen one is filled. Arrow keys
 * (and Home and End) move the choice, as in any radio group. Each option
 * shows its unread conversations.
 */
export default function SendersToggle({
  value,
  onChange,
  unread,
}: {
  value: Senders;
  onChange: (value: Senders) => void;
  unread: Record<Senders, number>;
}) {
  const buttons = useRef<Partial<Record<Senders, HTMLButtonElement | null>>>(
    {}
  );

  function onKeyDown(e: KeyboardEvent, from: number) {
    const to =
      e.key === "Home"
        ? 0
        : e.key === "End"
        ? OPTIONS.length - 1
        : e.key in NEXT
        ? (from + NEXT[e.key] + OPTIONS.length) % OPTIONS.length
        : -1;
    if (to < 0) return;
    e.preventDefault();
    onChange(OPTIONS[to].value);
    buttons.current[OPTIONS[to].value]?.focus();
  }

  return (
    <div
      role="radiogroup"
      aria-label="Senders"
      className="toggle-group toggle-group--green !bg-white !shadow-none"
    >
      {OPTIONS.map((option, i) => {
        const checked = value === option.value;
        const count = unread[option.value];
        return (
          <button
            key={option.value}
            ref={(el) => {
              buttons.current[option.value] = el;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            data-state={checked ? "on" : "off"}
            className="toggle-group--item !px-3 !py-1.5 !text-sm"
            onClick={() => onChange(option.value)}
            onKeyDown={(e) => onKeyDown(e, i)}
          >
            {option.label}
            {count > 0 && (
              <>
                <span
                  aria-hidden
                  className={`ml-2 inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded-full px-1 text-xs font-bold leading-none ${
                    checked
                      ? "bg-white text-blue-600"
                      : "bg-blue-500 text-white"
                  }`}
                >
                  {count}
                </span>
                <span className="sr-only">, {count} unread</span>
              </>
            )}
          </button>
        );
      })}
    </div>
  );
}
